import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from '@mui/material'
import { ClientSubtype } from 'feature/company/types/CompanyRegistrationForm'
import { FormInputText } from 'feature/formComponents'
import { useEffect, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import styles from '../CompanyRegistration.module.scss'

const INN_CONFIG: Record<
  ClientSubtype,
  { length: number; placeholder: string; regex: RegExp; error: string }
> = {
  llc: {
    length: 10,
    placeholder: '10 цифр',
    regex: /^\d{10}$/,
    error: 'ИНН должен содержать 10 цифр',
  },
  ie: {
    length: 12,
    placeholder: '12 цифр',
    regex: /^\d{12}$/,
    error: 'ИНН должен содержать 12 цифр',
  },
}

export function CompanyDataSection() {
  const { watch, setValue, trigger, getFieldState } = useFormContext()
  const clientSubtype = (watch('clientSubtype') as ClientSubtype) ?? 'ie'
  const innConfig = INN_CONFIG[clientSubtype]
  const isFirstRender = useRef(true)

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    const { isTouched, isDirty } = getFieldState('inn')
    if (isTouched || isDirty) {
      trigger('inn')
    }
  }, [clientSubtype, trigger, getFieldState])

  const handleSubtypeChange = (_: unknown, value: string) => {
    setValue('clientSubtype', value as ClientSubtype)
    if (value === 'ie') {
      setValue('kpp', null)
    }
  }

  return (
    <div className={styles.companyRegistration__section}>
      <div>
        <h3 className={styles.companyRegistration__sectionTitle}>
          Данные компании
        </h3>

        <FormControl>
          <FormLabel
            className={styles.companyRegistration__radioLabel}
            sx={{ '&.Mui-focused': { color: 'inherit' } }}
          >
            Тип юридического лица
          </FormLabel>
          <RadioGroup value={clientSubtype} onChange={handleSubtypeChange}>
            <FormControlLabel
              value='llc'
              control={<Radio size='small' />}
              label='Общество с ограниченной ответственностью (ООО)'
            />
            <FormControlLabel
              value='ie'
              control={<Radio size='small' />}
              label='Индивидуальный предприниматель (ИП)'
            />
          </RadioGroup>
        </FormControl>
      </div>

      <FormInputText
        name='companyName'
        allowAllChars
        inputProps={{
          label: 'Наименование организации',
          required: true,
          inputProps: { maxLength: 128 },
          'data-testid': 'company-reg-company-name',
        }}
      />
      <div className={styles.companyRegistration__row}>
        <FormInputText
          name='inn'
          numericOnly
          rules={{
            required: 'ИНН обязателен для заполнения',
            validate: (v: string) =>
              !v || innConfig.regex.test(v) || innConfig.error,
          }}
          inputProps={{
            label: 'ИНН',
            required: true,
            placeholder: innConfig.placeholder,
            inputProps: { maxLength: innConfig.length },
            'data-testid': 'company-reg-inn',
          }}
        />
        {clientSubtype === 'llc' && (
          <FormInputText
            name='kpp'
            numericOnly
            rules={{
              validate: (v: string) =>
                !v || /^\d{9}$/.test(v) || 'КПП должен содержать 9 цифр',
            }}
            inputProps={{
              label: 'КПП',
              placeholder: '9 цифр',
              inputProps: { maxLength: 9 },
              'data-testid': 'company-reg-kpp',
            }}
          />
        )}
      </div>
      <FormInputText
        name='ogrn'
        numericOnly
        rules={{
          required: 'ОГРН/ОГРНИП обязателен для заполнения',
          validate: (v: string) =>
            !v ||
            /^\d{13}$|^\d{15}$/.test(v) ||
            'ОГРН должен содержать 13 или 15 цифр',
        }}
        inputProps={{
          label: 'ОГРН/ОГРНИП',
          required: true,
          placeholder: '13 или 15 цифр',
          inputProps: { maxLength: 15 },
          'data-testid': 'company-reg-ogrn',
        }}
      />
    </div>
  )
}
